import axios, { AxiosError } from 'axios'
import { API_URL } from './config'
import { clearSession, getToken } from './session'

/* The one axios instance the storefront uses. Everything that crosses the
   network goes through it, so the base URL, the timeout and the shape of an
   error are decided once rather than at each call site. */

/** Re-exported so the many call sites that already read it from here keep
    working; the value itself lives in ./config — see the note there. */
export { API_URL }

export const http = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 20_000,
  headers: { Accept: 'application/json' },
})

/* Most of this site is public and sends no token at all. The account pages
   are the exception, and their requests are answered about whoever the token
   says is asking — which is why the customer id is never in a URL here.

   The token is read per request rather than set on the instance at sign-in:
   signing in and out then needs no reconfiguration, and a request already in
   flight when the session ended does not carry a stale one. */
http.interceptors.request.use((request) => {
  const token = getToken()
  if (token) request.headers.set('Authorization', `Bearer ${token}`)
  return request
})

/* One 401 means the session is over — expired overnight, or signed by a
   secret the server no longer holds. Clearing it here is what puts the
   sign-in form up: the account provider is subscribed to the change. A 403
   is deliberately left alone: it means the API knows exactly who this is and
   is refusing anyway, and signing somebody out over it would be the worst
   possible reading of a permission failure.

   The rejection still propagates, so each call site's own handling runs. */
http.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (error instanceof AxiosError && error.response?.status === 401) clearSession()
    return Promise.reject(error)
  },
)

/** A receipt or photo the API serves, as an absolute URL. */
export const assetUrl = (path: string) =>
  path.startsWith('http') ? path : `${API_URL}${path}`

/* What the API sends when it refuses a request: one sentence for the person,
   and — on a validation failure — a message per field for the form. */
export type ApiError = {
  message: string
  fields?: Record<string, string>
  status?: number
}

/** Turns anything thrown by axios into the shape a form can render. */
export function toApiError(error: unknown): ApiError {
  if (error instanceof AxiosError) {
    const data = error.response?.data as
      | { error?: string; errors?: Record<string, string> }
      | undefined

    if (data?.error) {
      return { message: data.error, fields: data.errors, status: error.response?.status }
    }

    /* No response at all is the API being down or unreachable — a different
       problem from a rejected request, and worth saying so plainly rather
       than showing the browser's own wording. */
    if (!error.response) {
      return {
        message:
          'We could not reach the showroom just now. Check your connection and try again.',
      }
    }

    return { message: error.message, status: error.response.status }
  }

  return { message: 'Something went wrong. Please try again.' }
}
